import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { Guid } from "@/Common/Models/Guid";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { SyncRvqImmunizationResponse } from "./ResponseModels/SyncRvqImmunizationResponse";

interface SaveMedicalItemImmunizationResponse {
    DeletedIds: string[];
    HasConcurrentEditOccured: boolean;
    ConcurrentEditTime: Date;
    ConcurrentEditUserId: string;
    NewEncounterId?: string;
    EMRTemplateId: string;
    EditedMedicalItemId: string;
    QuebecVaccinationRegistryErrors: ErrorMessage[];
    MedicalItems: MedicalItemImmunization[];
}

class RvqImmunizationSavingSyncManager {
    private patientContactId: Guid = null;
    private patientContactFacetId: Guid = null;
    private triggerIsSavingState: (isSaving: boolean) => void = null;

    public constructor(patientContactId: Guid, patientContactFacetId: Guid, triggerIsSavingState: (isSaving: boolean) => void) {
        this.patientContactId = patientContactId;
        this.patientContactFacetId = patientContactFacetId;
        this.triggerIsSavingState = triggerIsSavingState;
    }

    public async saveImmunization(immunization: MedicalItemImmunization, latestMedicalItemIdInTheEMRTemplateTheUserCurrentlySees: string, isSyncing: boolean, isPlus: boolean): Promise<void> {
        const response = await Client.postAsync<SaveMedicalItemImmunizationResponse>(
            ServerOperations.Server.Myle_Patient_MedicalItem_SaveMedicalItemImmunization,
            {
                PatientContactId: this.patientContactId,
                PatientContactFacetId: this.patientContactFacetId,
                MedicalItems: [immunization],
                SaveInNewEncounter: false,
                LatestMedicalItemIdInTheEMRTemplateTheUserCurrentlySees: latestMedicalItemIdInTheEMRTemplateTheUserCurrentlySees
            }
        );

        if (!response.isSuccess) {
            sessionStore.showPopup(response.errors);
            this.triggerIsSavingState(false);
            return;
        }

        let updatedItems = response?.data.MedicalItems;
        const emrTemplateId = response?.data.EMRTemplateId;
        const savedImmunization = response.data?.MedicalItems?.firstOrDefault(x => x.MedicalItemId === response?.data.EditedMedicalItemId);

        if (savedImmunization) {
            const encounterId = savedImmunization.RelatedEncounterId;
            if (encounterId)
                patientStore.updateContributorsEncountersData(this.patientContactId, encounterId);
        }

        updatedItems = updatedItems.upsert(savedImmunization, (a, b) => a.Id === b.Id);

        if (isSyncing) {
            const synced = await this.trySyncImmunizationHandler(savedImmunization, updatedItems, emrTemplateId, isPlus);
            if (!synced && isPlus) {
                this.triggerIsSavingState(false);
                return;
            }
        }

        this.updatePatientStoreModels(updatedItems, emrTemplateId);

        this.triggerIsSavingState(false);

        if (isPlus)
            sessionStore.dispatch(LocalPatientOperations.MedicalNote.Edit_NewImmunizationItem);
        else
            sessionStore.dispatch(LocalPatientOperations.MedicalItems.ReturnToNote);
    }

    public async trySyncImmunizationHandler(
        savedImmunization: MedicalItemImmunization,
        updatedItems: MedicalItemImmunization[],
        emrTemplateId: string,
        isPlus: boolean
    ): Promise<boolean> {
        const syncResponse = await this.submitImmunizationToRvq(savedImmunization.MedicalItemId, [savedImmunization]);

        if (!syncResponse.isSuccess) {
            if (isPlus)
                sessionStore.showPopup({
                    okLabel: cacheStore.GetString("QuebecVaccinationRegistry_BackToLastImmunization"),
                    okAction: () => {
                        this.updatePatientStoreModels(updatedItems, emrTemplateId);
                        sessionStore.dispatch(LocalPatientOperations.MedicalItems.Modify);
                    },
                    cancelAction: () => {
                        this.updatePatientStoreModels(updatedItems, emrTemplateId);
                        sessionStore.dispatch(LocalPatientOperations.MedicalItems.Modify);
                    },
                    altButtonAction: () => {
                        this.updatePatientStoreModels(updatedItems, emrTemplateId);
                        sessionStore.dispatch(LocalPatientOperations.MedicalNote.Edit_NewImmunizationItem);
                    },
                    altButtonLabel: cacheStore.GetString("QuebecVaccinationRegistry_NewImmunizationLabel"),
                    content: syncResponse.errors,
                    autoWidth: true,
                    popupClass: "max-width-1000px",
                    centerPopupHorizontally: true,
                    showCloseX: true
                });
            else
                sessionStore.showPopup(syncResponse.errors);

            return false;
        }

        savedImmunization.QuebecRegistryId = syncResponse.data?.Immunization?.Id ?? savedImmunization.QuebecRegistryId;
        savedImmunization.SubmittedToQuebecRegistryOn = syncResponse.data?.SubmittedToQuebecRegistryOn;

        updatedItems = updatedItems.upsert(savedImmunization, (a, b) => a.Id === b.Id);

        if (syncResponse.data.UpdatedMedicalItems?.any())
            updatedItems = updatedItems.upsert(syncResponse.data.UpdatedMedicalItems, (a, b) => a.Id === b.Id);

        if (syncResponse.data?.PartiallyFailedErrorMessage?.any()) {
            sessionStore.showPopup(
                [
                    cacheStore.GetString("QuebecVaccinationRegistry_FailedAntigensForRefusalVaccinationLabel"),
                    ...(syncResponse.data.PartiallyFailedErrorMessage || [])
                ]
            );
        }

        return true;
    }

    public updatePatientStoreModels(
        updatedItems: MedicalItemImmunization[],
        emrTemplateId: string
    ): void {
        const encounterId = updatedItems.firstOrDefault().RelatedEncounterId;

        sessionStore.dispatch(
            LocalPatientOperations.MedicalNote.UpdateImmunizationMedicalItemsAfterRvqSaveAndSync,
            {
                immunizations: updatedItems,
                encounterId,
                emrTemplateId
            }
        );
    }

    public async submitImmunizationToRvq(immunizationMedicalItemId: string, medicalItems: MedicalItemImmunization[]): Promise<ClientResponse<SyncRvqImmunizationResponse>> {
        return await Client.postAsync<SyncRvqImmunizationResponse>(
            ServerOperations.Server.QuebecVaccinationRegistry_SyncImmunization,
            {
                ImmunizationMedicalItemId: immunizationMedicalItemId,
                MedicalItems: medicalItems
            }
        );
    }
}

export { RvqImmunizationSavingSyncManager };
